/**
 * Utility functions for date formatting and conversion
 */

/**
 * Formats a date to European date format (dd.mm.yyyy) without time
 */
export function formatDateToEuropean(isoDate: string | null | undefined): string {
  if (!isoDate) return '';

  try {
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) return '';

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}.${month}.${year}`;
  } catch {
    return '';
  }
}

/**
 * Formats a date to European datetime format (dd.mm.yyyy HH:mm)
 */
export function formatDateTimeEuropean(isoDate: string | null | undefined): string {
  if (!isoDate) return '';

  try {
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) return '';

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${day}.${month}.${year} ${hours}:${minutes}`;
  } catch {
    return '';
  }
}

// --- Timezone utilities ---

/**
 * Format a date in a specific timezone using Intl.DateTimeFormat
 */
export function formatDateInTimezone(
  isoDate: string | null | undefined,
  timezone: string,
  language: string,
  opts?: Intl.DateTimeFormatOptions
): string {
  if (!isoDate) return '';
  try {
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) return '';
    const locale = language === 'uk' ? 'uk-UA' : 'en-US';
    return new Intl.DateTimeFormat(locale, { timeZone: timezone, ...opts }).format(date);
  } catch {
    return '';
  }
}

/**
 * Format time (HH:mm) in a specific timezone. Returns null for midnight (00:00).
 */
export function formatTimeInTimezone(isoDate: string | null | undefined, timezone: string): string | null {
  if (!isoDate) return null;
  try {
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) return null;
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(date);
    const h = parts.find(p => p.type === 'hour')?.value || '00';
    const m = parts.find(p => p.type === 'minute')?.value || '00';
    if (h === '00' && m === '00') return null;
    return `${h}:${m}`;
  } catch {
    return null;
  }
}

/**
 * Get timezone abbreviation like "EET", "EEST", "EST", etc.
 */
export function getTimezoneAbbreviation(timezone: string, date?: Date): string {
  try {
    const d = date || new Date();
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    }).formatToParts(d);
    return parts.find(p => p.type === 'timeZoneName')?.value || timezone;
  } catch {
    return timezone;
  }
}

/** Both IANA spellings of the Ukrainian zone are in `content.timezone`. */
const KYIV_ZONES = new Set(['Europe/Kyiv', 'Europe/Kiev']);

/**
 * Human label naming the zone a printed event time belongs to, e.g.
 * "за київським часом (GMT+3)" / "Kyiv time (GMT+3)".
 *
 * An emailed start time with no zone attached is a trap: the reader assumes
 * their own clock. The offset is resolved against the event's own date so it is
 * DST-correct (Kyiv is GMT+3 in September, GMT+2 in December), and non-Kyiv
 * zones fall back to the city name so the label is never silently wrong.
 */
export function formatEventTimezoneLabel(
  timezone: string,
  language: string,
  atDate?: Date | string | null
): string {
  const isUk = language === 'uk';
  try {
    const reference = atDate ? new Date(atDate) : new Date();
    const date = isNaN(reference.getTime()) ? new Date() : reference;

    const offset = new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone,
      timeZoneName: 'shortOffset',
    })
      .formatToParts(date)
      .find(p => p.type === 'timeZoneName')?.value || '';

    const zoneName = KYIV_ZONES.has(timezone)
      ? (isUk ? 'за київським часом' : 'Kyiv time')
      : timezone.split('/').pop()?.replace(/_/g, ' ') || timezone;

    return offset ? `${zoneName} (${offset})` : zoneName;
  } catch {
    return timezone;
  }
}

/**
 * Save path: Convert a browser-local Date to UTC as if the time was in the event timezone.
 *
 * Example: Organizer in NYC picks "10:00" → JS creates Date with 10:00 EST.
 * If event timezone is Europe/Kyiv, we want UTC to represent 10:00 EET, not 10:00 EST.
 * So we compute the offset difference and adjust.
 */
export function localDateToEventTimezoneISO(date: Date | null, eventTimezone: string): string | null {
  if (!date) return null;
  try {
    // Get the date/time components as they appear in the browser (what the user typed)
    const y = date.getFullYear();
    const mo = date.getMonth();
    const d = date.getDate();
    const h = date.getHours();
    const mi = date.getMinutes();

    // Build a formatter that shows time in the event timezone
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: eventTimezone,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false,
    });

    // Find what UTC time produces the same wall-clock reading in the event timezone
    // Start with a rough guess: use the same components as UTC
    let guess = new Date(Date.UTC(y, mo, d, h, mi, 0, 0));

    // Iterate to converge (max 3 iterations)
    for (let i = 0; i < 3; i++) {
      const parts = formatter.formatToParts(guess);
      const gY = parseInt(parts.find(p => p.type === 'year')!.value);
      const gMo = parseInt(parts.find(p => p.type === 'month')!.value) - 1;
      const gD = parseInt(parts.find(p => p.type === 'day')!.value);
      const gH = parseInt(parts.find(p => p.type === 'hour')!.value);
      const gMi = parseInt(parts.find(p => p.type === 'minute')!.value);

      const diffMs =
        new Date(y, mo, d, h, mi).getTime() -
        new Date(gY, gMo, gD, gH, gMi).getTime();

      if (diffMs === 0) break;
      guess = new Date(guess.getTime() + diffMs);
    }

    return guess.toISOString();
  } catch {
    return date.toISOString();
  }
}

/**
 * Load path: Convert a UTC ISO string to a Date that, when displayed by the browser's
 * DateTimePicker (which uses browser TZ), shows the correct event-timezone time.
 *
 * Example: DB has "2025-03-15T08:00:00Z" (10:00 EET). If browser is in NYC (EST),
 * `new Date(iso)` would show 03:00. We want the picker to show 10:00,
 * so we create a Date whose local representation is 10:00.
 */
export function eventTimezoneISOToLocalDate(isoString: string | null, eventTimezone: string): Date | null {
  if (!isoString) return null;
  try {
    const utcDate = new Date(isoString);
    if (isNaN(utcDate.getTime())) return null;

    // Get the wall-clock time in the event timezone
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: eventTimezone,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false,
    }).formatToParts(utcDate);

    const y = parseInt(parts.find(p => p.type === 'year')!.value);
    const mo = parseInt(parts.find(p => p.type === 'month')!.value) - 1;
    const d = parseInt(parts.find(p => p.type === 'day')!.value);
    const h = parseInt(parts.find(p => p.type === 'hour')!.value);
    const mi = parseInt(parts.find(p => p.type === 'minute')!.value);

    // Create a Date in the browser's local timezone with these components
    // so the DateTimePicker shows the event-timezone time
    return new Date(y, mo, d, h, mi);
  } catch {
    return new Date(isoString);
  }
}

/**
 * Check if the viewer's browser timezone differs from the event timezone.
 * Compares by UTC offset at the current moment (handles DST correctly).
 */
export function isViewerInDifferentTimezone(eventTimezone: string | null | undefined): boolean {
  if (!eventTimezone) return false;
  try {
    const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (browserTz === eventTimezone) return false;

    // Compare offsets – same offset means effectively same timezone for display
    const now = new Date();
    const browserOffset = getTimezoneOffsetMinutes(browserTz, now);
    const eventOffset = getTimezoneOffsetMinutes(eventTimezone, now);
    return browserOffset !== eventOffset;
  } catch {
    return false;
  }
}

/**
 * Get the UTC offset in minutes for a timezone at a given date.
 */
function getTimezoneOffsetMinutes(timezone: string, date: Date): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(date);

  const y = parseInt(parts.find(p => p.type === 'year')!.value);
  const mo = parseInt(parts.find(p => p.type === 'month')!.value) - 1;
  const d = parseInt(parts.find(p => p.type === 'day')!.value);
  const h = parseInt(parts.find(p => p.type === 'hour')!.value);
  const mi = parseInt(parts.find(p => p.type === 'minute')!.value);

  // Wall-clock time in the timezone as if it were UTC
  const tzAsUtc = Date.UTC(y, mo, d, h, mi);
  // Actual UTC time
  const utc = date.getTime();
  return Math.round((tzAsUtc - utc) / 60000);
}
